/**
 * StepPromptsScreen — "Prompts das Jornadas" (tela dedicada).
 *
 * Irmã da feature "Tokens por Step" (card em Configurações), mas em tela
 * própria — o dono edita o PROMPT DE SISTEMA base de cada etapa da jornada
 * (Design, Tecnologia, Análise, Backlog, Squad, Conferência, Wizard).
 *
 * Ligação com a API (contrato construído em paralelo pelo backend):
 *   GET /settings/step-prompts → { steps: [{ key, label, phase, default_text,
 *                                             override, effective }] }
 *   PUT /settings/step-prompts → body { steps: [{ key, prompt_text }] }
 *                                 prompt_text null/vazio = remove o override.
 *                                 Requer a MESMA permissão de escrita do card
 *                                 "Tokens por Step"/Orçamento (Admin/Manager);
 *                                 403 → tela read-only + aviso.
 *
 * Preserva identidade visual Nava: paleta C, Card/Btn/Badge, copy pt-BR.
 * Acessibilidade: labels associados, foco visível herdado do reset global,
 * `role="status"`/`role="alert"` nos avisos.
 */
import { useMemo, useState } from 'react';
import { FileText, ChevronRight, RotateCcw, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { C } from '@/theme/palette';
import { Card, Btn, Badge } from '@/components/ui';
import { LoadingSpinner, QueryError, MutationError } from '@/components/ui/QueryState';
import { useStepPrompts, useSaveStepPrompts } from '@/api/stepPrompts';

const TEXTAREA_STYLE = {
  width: '100%',
  minHeight: 220, // ~12-16 linhas monoespaçadas
  padding: '10px 12px',
  backgroundColor: C.black,
  color: C.neutral,
  fontFamily: "'JetBrains Mono', 'SF Mono', Menlo, Consolas, monospace",
  fontSize: 12.5,
  lineHeight: 1.6,
  borderRadius: 6,
  resize: 'vertical',
  boxSizing: 'border-box',
  outline: 'none',
};

/** Um step individual: textarea editável + ações (restaurar padrão / ver padrão). */
function StepPromptRow({ st, value, changed, forbidden, onChange, onRestore, restoring }) {
  const [showDefault, setShowDefault] = useState(false);
  const hasOverride = st.override != null && st.override !== '';

  return (
    <div style={{ padding: '14px 0', borderTop: `1px solid ${C.blackBorder}` }}>
      <div className="flex items-center justify-between gap-2 mb-2" style={{ flexWrap: 'wrap' }}>
        <div className="flex items-center gap-2" style={{ minWidth: 0 }}>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: C.neutral }}>{st.label}</span>
          {hasOverride && <Badge color={C.cyber} light>customizado</Badge>}
          {changed && <Badge color={C.digital} light>não salvo</Badge>}
        </div>
        <div className="flex items-center gap-1" style={{ flexShrink: 0 }}>
          <Btn
            variant="ghost"
            size="sm"
            icon={showDefault ? EyeOff : Eye}
            onClick={() => setShowDefault((s) => !s)}
          >
            {showDefault ? 'Ocultar padrão' : 'Ver padrão do sistema'}
          </Btn>
          {hasOverride && (
            <Btn
              variant="ghost"
              size="sm"
              icon={RotateCcw}
              onClick={() => onRestore(st.key)}
              disabled={forbidden || restoring}
            >
              {restoring ? 'Restaurando...' : 'Restaurar padrão'}
            </Btn>
          )}
        </div>
      </div>

      <label htmlFor={`step-prompt-${st.key}`} className="sr-only" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}>
        Prompt-base para {st.label}
      </label>
      <textarea
        id={`step-prompt-${st.key}`}
        value={value}
        onChange={(e) => onChange(st.key, e.target.value)}
        disabled={forbidden}
        rows={14}
        spellCheck={false}
        style={{
          ...TEXTAREA_STYLE,
          border: `1px solid ${changed ? C.cyber : C.blackBorder}`,
          opacity: forbidden ? 0.7 : 1,
        }}
      />

      {showDefault && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 11, color: C.neutralMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Padrão do sistema (código)
          </div>
          <textarea
            readOnly
            value={st.default_text ?? ''}
            rows={14}
            spellCheck={false}
            style={{
              ...TEXTAREA_STYLE,
              border: `1px solid ${C.blackBorder}`,
              color: C.neutralDim,
              backgroundColor: C.blackElev2 ?? C.black,
            }}
          />
        </div>
      )}
    </div>
  );
}

export function StepPromptsScreen() {
  const { data, isLoading, error, refetch } = useStepPrompts();
  const save = useSaveStepPrompts();
  const [edits, setEdits] = useState({});
  const [openPhases, setOpenPhases] = useState({});
  const [forbidden, setForbidden] = useState(false);
  const [savedMsg, setSavedMsg] = useState(false);
  const [restoringKey, setRestoringKey] = useState(null);

  const steps = Array.isArray(data?.steps) ? data.steps : [];

  // Agrupa por fase preservando a ordem de chegada do backend.
  const groups = useMemo(() => {
    const map = new Map();
    steps.forEach((st) => {
      const phase = st.phase || 'Geral';
      if (!map.has(phase)) map.set(phase, []);
      map.get(phase).push(st);
    });
    return Array.from(map.entries());
  }, [steps]);

  const isOpen = (phase) => openPhases[phase] ?? true;
  const togglePhase = (phase) => setOpenPhases((s) => ({ ...s, [phase]: !isOpen(phase) }));

  const valueOf = (st) => (Object.prototype.hasOwnProperty.call(edits, st.key)
    ? edits[st.key]
    : (st.override ?? st.default_text ?? ''));

  const onChange = (key, v) => {
    setSavedMsg(false);
    setEdits((s) => ({ ...s, [key]: v }));
  };

  // Um step é "dirty" quando o texto atual difere do que foi carregado
  // (override, ou o default caso não haja override).
  const dirtyKeys = Object.keys(edits).filter((key) => {
    const st = steps.find((s) => s.key === key);
    if (!st) return false;
    const loaded = st.override ?? st.default_text ?? '';
    return edits[key] !== loaded;
  });

  const handleError = (err) => { if (err?.status === 403) setForbidden(true); };

  const onRestore = (key) => {
    setSavedMsg(false);
    setRestoringKey(key);
    save.mutate([{ key, prompt_text: null }], {
      onSuccess: () => {
        setEdits((s) => { const c = { ...s }; delete c[key]; return c; });
        setSavedMsg(true);
        setRestoringKey(null);
      },
      onError: (err) => { handleError(err); setRestoringKey(null); },
    });
  };

  const onSave = () => {
    setSavedMsg(false);
    const payload = dirtyKeys.map((key) => {
      const raw = edits[key];
      const trimmed = typeof raw === 'string' ? raw : '';
      return { key, prompt_text: trimmed.trim() ? raw : null };
    });
    if (!payload.length) return;
    save.mutate(payload, {
      onSuccess: () => { setEdits({}); setSavedMsg(true); },
      onError: handleError,
    });
  };

  const dirtyCount = dirtyKeys.length;

  return (
    <div style={{ padding: 28, maxWidth: 1100 }}>
      <div className="flex items-center justify-between" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: C.neutral, letterSpacing: '-0.02em', margin: 0 }}>
            Prompts das Jornadas
          </h1>
          <p style={{ fontSize: 13, color: C.neutralDim, marginTop: 4, maxWidth: 720 }}>
            Edite o prompt-base de cada etapa. Vazio = usa o padrão do sistema. As skills do
            agente e diretivas (brownfield) continuam sendo anexadas automaticamente.
          </p>
        </div>
        {!isLoading && !error && (
          <Btn
            variant="primary"
            onClick={onSave}
            disabled={forbidden || save.isPending || dirtyCount === 0}
          >
            {save.isPending ? 'Salvando...' : `Salvar alterações${dirtyCount ? ` (${dirtyCount})` : ''}`}
          </Btn>
        )}
      </div>

      <div
        className="flex items-start gap-2"
        style={{
          marginTop: 14,
          marginBottom: 20,
          padding: '10px 14px',
          borderRadius: 8,
          backgroundColor: `${C.digital}11`,
          border: `1px solid ${C.digital}33`,
        }}
      >
        <AlertTriangle size={14} style={{ color: C.digital, flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
        <p style={{ fontSize: 12, color: C.neutralDim, margin: 0 }}>
          Prompts malformados podem degradar a geração. O texto real por chamada ainda respeita o
          teto de tokens do modelo.
        </p>
      </div>

      {isLoading ? (
        <LoadingSpinner label="Carregando prompts das jornadas..." />
      ) : error ? (
        <QueryError error={error} onRetry={refetch} />
      ) : (
        <>
          {forbidden && (
            <div role="alert" style={{ fontSize: 12.5, color: C.hot, marginBottom: 14 }}>
              Sem permissão para editar (requer Admin ou Manager). Exibindo os prompts em modo
              somente leitura.
            </div>
          )}
          {savedMsg && !forbidden && (
            <div role="status" style={{ fontSize: 12.5, color: C.cyber, marginBottom: 14 }}>
              Alterações salvas.
            </div>
          )}
          {save.isError && !forbidden && (
            <div style={{ marginBottom: 14 }}>
              <MutationError error={save.error} onDismiss={() => save.reset()} />
            </div>
          )}

          {groups.length === 0 && (
            <Card>
              <div style={{ fontSize: 12.5, color: C.neutralMuted }}>Nenhum step configurável ainda.</div>
            </Card>
          )}

          {groups.map(([phase, list]) => (
            <Card key={phase} style={{ marginBottom: 14 }}>
              <button
                type="button"
                onClick={() => togglePhase(phase)}
                aria-expanded={isOpen(phase)}
                className="flex items-center justify-between"
                style={{ width: '100%', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                <div className="flex items-center gap-2">
                  <FileText size={15} style={{ color: C.cyber }} aria-hidden="true" />
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: C.neutral, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {phase}
                  </span>
                  <span style={{ fontSize: 11.5, color: C.neutralMuted }}>
                    ({list.length} {list.length === 1 ? 'step' : 'steps'})
                  </span>
                </div>
                <ChevronRight
                  size={16}
                  aria-hidden="true"
                  style={{
                    color: C.neutralMuted,
                    transform: isOpen(phase) ? 'rotate(90deg)' : 'none',
                    transition: 'transform 150ms',
                  }}
                />
              </button>

              {isOpen(phase) && (
                <div>
                  {list.map((st) => (
                    <StepPromptRow
                      key={st.key}
                      st={st}
                      value={valueOf(st)}
                      changed={dirtyKeys.includes(st.key)}
                      forbidden={forbidden}
                      onChange={onChange}
                      onRestore={onRestore}
                      restoring={restoringKey === st.key && save.isPending}
                    />
                  ))}
                </div>
              )}
            </Card>
          ))}

          {groups.length > 0 && (
            <div className="flex items-center justify-end" style={{ marginTop: 4 }}>
              <Btn
                variant="primary"
                onClick={onSave}
                disabled={forbidden || save.isPending || dirtyCount === 0}
              >
                {save.isPending ? 'Salvando...' : `Salvar alterações${dirtyCount ? ` (${dirtyCount})` : ''}`}
              </Btn>
            </div>
          )}
        </>
      )}
    </div>
  );
}
